import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import { creditsService } from "../services/creditsService";
import { light as C, MONO, radius, injectKeyframes } from "../../hiring/theme";

// Kumudu handing AI credits to customer stores.
//
// WHAT THIS SCREEN IS FOR
// Kumudu pays the provider; every other store draws on an allowance. Without
// this page that allowance can only be changed with a SQL statement, which
// means in practice it never gets changed and somebody quietly turns the limit
// off instead.
//
// The list is ordered by who needs attention, not by store id. A page sorted by
// id makes you read all of it to find the one store that is about to stop
// working — which is the only reason anybody opens this.

const ACT_HOVER = "#115e59";

const n = (v) => Number(v || 0).toLocaleString("en-IN");

/** Rupees, for the cost figures. The provider bills USD; this is orientation. */
const inr = (usd, rate = 88) => {
  const v = Number(usd || 0) * rate;
  if (v === 0) return "₹0";
  if (v >= 100) return `₹${v.toFixed(0)}`;
  if (v >= 1) return `₹${v.toFixed(2)}`;
  return `₹${v.toFixed(3)}`;
};

const Label = ({ children, sx }) => (
  <Typography
    sx={{
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: ".1em",
      textTransform: "uppercase",
      color: C.faint,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/**
 * How worried should somebody be about this store.
 *
 * Three states, and the thresholds are in interviews rather than credits
 * because that is the unit the number means something in. "40 credits" is
 * abstract; "two interviews left" is a decision.
 */
function health(store, prices) {
  if (store.exempt) return { key: "EXEMPT", label: store.exempt_reason || "Not billed", tone: C.muted, bg: C.panel };
  const perInterview = prices?.INTERVIEW || 20;
  const left = Math.floor(store.balance / perInterview);
  if (store.balance <= 0) return { key: "EMPTY", label: "Out of credits", tone: C.negative, bg: C.negativeSoft };
  if (left <= 2) return { key: "LOW", label: `${left} interview${left === 1 ? "" : "s"} left`, tone: C.caution, bg: C.cautionSoft };
  return { key: "OK", label: `${left} interviews left`, tone: C.positive, bg: C.positiveSoft };
}

// Stores that cannot work come first, then the nearly-empty, then everyone
// else. Exempt stores sink to the bottom: they can never need anything.
const URGENCY = { EMPTY: 0, LOW: 1, OK: 2, EXEMPT: 3 };

export default function PlatformCredits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [granting, setGranting] = useState(null); // the store being granted to
  const [ledgerFor, setLedgerFor] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => injectKeyframes(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await creditsService.listStores();
      setData(res?.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "This page is for the platform owner. Your account cannot see it."
          : err?.response?.data?.statusMessage || "Could not load stores.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stores = (data?.stores || [])
    .map((s) => ({ ...s, health: health(s, data?.prices) }))
    .sort(
      (a, b) =>
        URGENCY[a.health.key] - URGENCY[b.health.key] || a.balance - b.balance,
    );

  const needAttention = stores.filter((s) => ["EMPTY", "LOW"].includes(s.health.key));

  if (loading && !data) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: 380 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={24} sx={{ color: C.act }} />
          <Label>Loading stores</Label>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", pb: 8 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "flex-end" }}
        spacing={2}
        sx={{ mb: 3.5 }}
      >
        <Box sx={{ flex: 1 }}>
          <Label>Platform</Label>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: { xs: 24, md: 30 },
              fontWeight: 700,
              letterSpacing: "-.02em",
              color: C.ink,
            }}
          >
            AI credits
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13.5, color: C.muted, lineHeight: 1.6 }}>
            Kumudu pays the provider. Every other store spends from an allowance
            you grant here.
            {data?.prices && (
              <>
                {" "}An interview costs {data.prices.INTERVIEW} credits, a resume
                screen {data.prices.RESUME_SCREEN}.
              </>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* The reason this page looked empty: a store only appears once it
              has hiring, and granting credits is what gives it hiring. */}
          <Button
            variant="contained"
            disableElevation
            startIcon={<StorefrontRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setAdding(true)}
            sx={{
              textTransform: "none",
              fontWeight: 650,
              borderRadius: radius.pill,
              px: 2.25,
              background: C.act,
              boxShadow: "none",
              "&:hover": { background: ACT_HOVER, boxShadow: "none" },
            }}
          >
            Add a store
          </Button>
          <Button
            onClick={load}
            disabled={loading}
            sx={{ minWidth: 0, px: 1, color: C.muted }}
          >
            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
          </Button>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2, height: 2, borderRadius: 99 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: radius.md }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          onClose={() => setNotice("")}
          sx={{ mb: 3, borderRadius: radius.md }}
        >
          {notice}
        </Alert>
      )}

      {/* The only reason to open this page. Surfaced above the list so it does
          not have to be found by reading. */}
      {needAttention.length > 0 && (
        <Alert
          icon={<WarningAmberRoundedIcon />}
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: radius.md,
            bgcolor: C.cautionSoft,
            color: C.body,
            "& .MuiAlert-icon": { color: C.caution },
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: C.ink }}>
            {needAttention.length} store{needAttention.length === 1 ? "" : "s"} cannot
            keep interviewing
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 0.25 }}>
            {needAttention.map((s) => s.name).join(", ")}
          </Typography>
        </Alert>
      )}

      {/* A store appears here only once it has hiring, and it gets hiring by
          being granted credits. Without saying so, a page listing only Kumudu
          reads as broken rather than as empty. */}
      {stores.filter((s) => !s.is_platform).length === 0 && !error && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            textAlign: "center",
            borderRadius: radius.lg,
            border: `1px dashed ${C.line}`,
            bgcolor: C.card,
          }}
        >
          <StorefrontRoundedIcon sx={{ fontSize: 28, color: C.faint }} />
          <Typography sx={{ mt: 1, fontSize: 15, fontWeight: 650, color: C.ink }}>
            No customer store uses hiring yet
          </Typography>
          <Typography
            sx={{ mt: 0.75, fontSize: 13.5, color: C.muted, maxWidth: 460, mx: "auto", lineHeight: 1.6 }}
          >
            Granting a store credits is what switches hiring on for it. Pick one
            and give it an allowance to get started.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            startIcon={<StorefrontRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setAdding(true)}
            sx={{
              mt: 2.5,
              textTransform: "none",
              fontWeight: 650,
              borderRadius: radius.pill,
              px: 3,
              background: C.act,
              boxShadow: "none",
              "&:hover": { background: ACT_HOVER, boxShadow: "none" },
            }}
          >
            Add a store
          </Button>
        </Paper>
      )}

      {stores.map((s) => (
        <Paper
          key={s.store_id}
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.75 },
            mb: 1.5,
            borderRadius: radius.lg,
            border: `1px solid ${s.health.key === "EMPTY" ? C.negativeSoft : C.hair}`,
            bgcolor: C.card,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.ink }} noWrap>
                  {s.name}
                </Typography>
                {s.is_platform && (
                  <Chip
                    size="small"
                    label="Kumudu"
                    sx={{ height: 19, fontSize: 10.5, fontWeight: 700, bgcolor: C.actSoft, color: C.act }}
                  />
                )}
                {s.key_mode === "BYOK" && (
                  <Chip
                    size="small"
                    label="own key"
                    sx={{ height: 19, fontSize: 10.5, fontWeight: 650, bgcolor: C.panel, color: C.muted }}
                  />
                )}
              </Stack>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, rowGap: 0.25 }}>
                <Meta k="store" v={s.store_id} />
                {!s.exempt && <Meta k="granted" v={n(s.granted_all_time)} />}
                {!s.exempt && <Meta k="used" v={n(s.spent_all_time)} />}
                {s.per_day && <Meta k="per day" v={s.per_day} />}
                {s.cost_usd > 0 && <Meta k="cost to us" v={inr(s.cost_usd)} />}
              </Stack>
            </Box>

            {/* Balance and state, together — the number means nothing without
                how long it lasts. */}
            <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: s.exempt ? C.faint : s.health.tone,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.exempt ? "—" : n(s.balance)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.muted }}>
                {s.health.label}
                {s.days_left != null && !s.exempt ? ` · ~${s.days_left}d` : ""}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                onClick={() => setLedgerFor(s)}
                sx={{ textTransform: "none", fontWeight: 650, color: C.muted, minWidth: 0 }}
              >
                <HistoryRoundedIcon sx={{ fontSize: 18 }} />
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                disabled={s.is_platform}
                onClick={() => setGranting(s)}
                sx={{
                  textTransform: "none",
                  fontWeight: 650,
                  borderRadius: radius.pill,
                  px: 2,
                  background: C.act,
                  boxShadow: "none",
                  "&:hover": { background: ACT_HOVER, boxShadow: "none" },
                  "&.Mui-disabled": { background: C.panel, color: C.faint },
                }}
              >
                Grant
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ))}

      {granting && (
        <GrantDialog
          store={granting}
          prices={data?.prices}
          onClose={() => setGranting(null)}
          onDone={(msg) => {
            setGranting(null);
            setNotice(msg);
            load();
          }}
        />
      )}
      {ledgerFor && <LedgerDialog store={ledgerFor} onClose={() => setLedgerFor(null)} />}
      {adding && (
        <AddStoreDialog
          onClose={() => setAdding(false)}
          onPick={(s) => {
            setAdding(false);
            // Straight into the grant dialog. Picking a store and giving it an
            // allowance are one action, not two screens.
            setGranting({ store_id: s.store_id, name: s.name, balance: 0 });
          }}
        />
      )}
    </Box>
  );
}

const Meta = ({ k, v }) => (
  <Stack direction="row" spacing={0.6} alignItems="baseline">
    <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: C.faint, letterSpacing: ".06em" }}>
      {String(k).toUpperCase()}
    </Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.body }}>{v}</Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────

function GrantDialog({ store, prices, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const value = Number(amount);
  const valid = Number.isInteger(value) && value !== 0 && note.trim().length > 0;
  const perInterview = prices?.INTERVIEW || 20;

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      const res = await creditsService.grant(store.store_id, value, note.trim());
      onDone(res?.data?.statusMessage || "Credits granted.");
    } catch (e) {
      setErr(e?.response?.data?.statusMessage || "Could not grant credits.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: radius.lg } }}>
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Label>{store.name}</Label>
            <Typography sx={{ mt: 0.5, fontSize: 19, fontWeight: 700, color: C.ink }}>
              Grant credits
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25 }}>
              Balance is {n(store.balance)}. Use a negative number to take credits back.
            </Typography>
          </Box>
          <Button onClick={onClose} disabled={saving} sx={{ minWidth: 0, p: 0.5, color: C.faint }}>
            <CloseRoundedIcon sx={{ fontSize: 19 }} />
          </Button>
        </Stack>

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Credits"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ mb: 0.75 }}
        />
        {/* What the number actually buys. A grant in credits is meaningless
            without it, and the mistake this prevents is granting 20 when you
            meant 20 interviews. */}
        {Number.isInteger(value) && value > 0 && (
          <Typography sx={{ fontSize: 12.5, color: C.act, mb: 1.5, fontWeight: 600 }}>
            ≈ {Math.floor(value / perInterview)} interviews
            {value % perInterview ? ` (${value % perInterview} credits over)` : ""}
          </Typography>
        )}

        <TextField
          fullWidth
          size="small"
          label="Why"
          placeholder="Q1 pack, ERP contract"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          helperText="Required. The only record of why this store was given an allowance."
          sx={{ mt: 1 }}
        />

        {err && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: radius.md, fontSize: 13 }}>
            {err}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: C.muted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!valid || saving}
            onClick={submit}
            sx={{
              textTransform: "none",
              fontWeight: 650,
              borderRadius: radius.pill,
              px: 3,
              background: C.act,
              boxShadow: "none",
              "&:hover": { background: ACT_HOVER, boxShadow: "none" },
            }}
          >
            {saving ? "Saving…" : value < 0 ? "Revoke" : "Grant"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LedgerDialog({ store, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    creditsService
      .ledger(store.store_id)
      .then((r) => setRows(r?.data?.data?.entries || []))
      .catch((e) => setErr(e?.response?.data?.statusMessage || "Could not load the ledger."));
  }, [store.store_id]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: radius.lg } }}>
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Label>{store.name}</Label>
            <Typography sx={{ mt: 0.5, fontSize: 19, fontWeight: 700, color: C.ink }}>
              Where the credits went
            </Typography>
          </Box>
          <Button onClick={onClose} sx={{ minWidth: 0, p: 0.5, color: C.faint }}>
            <CloseRoundedIcon sx={{ fontSize: 19 }} />
          </Button>
        </Stack>

        {err && <Alert severity="error" sx={{ borderRadius: radius.md }}>{err}</Alert>}
        {!rows && !err && <LinearProgress sx={{ height: 2, borderRadius: 99 }} />}
        {rows?.length === 0 && (
          <Typography sx={{ py: 4, textAlign: "center", fontSize: 13.5, color: C.muted }}>
            Nothing yet — no credits granted or spent.
          </Typography>
        )}

        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {(rows || []).map((r) => (
            <Box key={r.id} sx={{ py: 1.25, borderBottom: `1px solid ${C.hair}` }}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: r.credits > 0 ? C.positive : C.body,
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 54,
                  }}
                >
                  {r.credits > 0 ? "+" : ""}
                  {n(r.credits)}
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>
                  {r.reason}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11.5, color: C.faint, fontVariantNumeric: "tabular-nums" }}>
                  {n(r.balance_after)} left
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ pl: "62px", mt: 0.25 }} flexWrap="wrap">
                <Typography sx={{ fontSize: 11.5, color: C.faint }}>
                  {new Date(r.created_at).toLocaleString()}
                </Typography>
                {/* What it cost Kumudu, against what the store was charged.
                    Only on rows where the true figure was recorded. */}
                {r.cost_usd != null && r.cost_usd > 0 && (
                  <Typography sx={{ fontSize: 11.5, color: C.faint }}>
                    cost {inr(r.cost_usd)}
                  </Typography>
                )}
                {r.note && (
                  <Typography sx={{ fontSize: 12, color: C.muted }}>{r.note}</Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────────

/**
 * Choosing a store that does not have hiring yet.
 *
 * Searched, not listed. There are hundreds of stores and one of them uses
 * hiring; a dropdown would be a scroll through customers who are not the one
 * you mean. The search matches the name or the store id, because whoever is
 * doing this knows one or the other.
 */
function AddStoreDialog({ onClose, onPick }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    // Debounced: this fires on every keystroke otherwise, and the later
    // response can arrive before an earlier one and overwrite it.
    const t = setTimeout(async () => {
      try {
        const res = await creditsService.availableStores(search);
        if (cancelled) return;
        setRows(res?.data?.data?.stores || []);
        setTotal(res?.data?.data?.total_not_set_up ?? null);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.statusMessage || "Could not load stores.");
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: radius.lg } }}>
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Label>Platform</Label>
            <Typography sx={{ mt: 0.5, fontSize: 19, fontWeight: 700, color: C.ink }}>
              Add a store
            </Typography>
            <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25, lineHeight: 1.55 }}>
              Granting credits switches hiring on for the store.
              {total != null && ` ${total} stores do not have it yet.`}
            </Typography>
          </Box>
          <Button onClick={onClose} sx={{ minWidth: 0, p: 0.5, color: C.faint }}>
            <CloseRoundedIcon sx={{ fontSize: 19 }} />
          </Button>
        </Stack>

        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search by name or store id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchRoundedIcon sx={{ fontSize: 18, color: C.faint, mr: 1 }} />
            ),
          }}
        />

        {err && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: radius.md, fontSize: 13 }}>
            {err}
          </Alert>
        )}
        {!rows && !err && <LinearProgress sx={{ mt: 2, height: 2, borderRadius: 99 }} />}
        {rows?.length === 0 && (
          <Typography sx={{ py: 4, textAlign: "center", fontSize: 13.5, color: C.muted }}>
            {search ? "No store matches that." : "Every store already has hiring."}
          </Typography>
        )}

        <Box sx={{ mt: 1, maxHeight: 340, overflowY: "auto" }}>
          {(rows || []).map((s) => (
            <Box
              key={s.store_id}
              onClick={() => onPick(s)}
              sx={{
                py: 1.25,
                px: 1,
                borderRadius: radius.sm,
                cursor: "pointer",
                borderBottom: `1px solid ${C.hair}`,
                "&:hover": { bgcolor: C.actSoft },
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 650, color: C.ink }} noWrap>
                {s.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.faint }}>
                store {s.store_id}
                {s.owner ? ` · ${s.owner}` : ""}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 25 at a time. Without this, a short list looks like the whole list. */}
        {rows && total != null && rows.length < total && (
          <Typography sx={{ mt: 1.5, fontSize: 12, color: C.faint, textAlign: "center" }}>
            Showing {rows.length} of {total}. Search to narrow it down.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
